import {HalContext, HalImage} from "@cm/hal"
import {HalPainterImageCompositorRef} from "@cm/hal/hal-ref"

export class ArithmeticHelperHal {
    constructor(private halContext: HalContext) {
        this._data = {
            compositorScale: halContext.createHalPainterImageCompositor(
                `
            uniform float u_scale;
            
            vec4 computeColor(ivec2 targetPixel) {
                vec4 a = texelFetch0(targetPixel, ADDRESS_MODE_REPEAT);
                return a * u_scale;
            }
        `,
            ),
            compositorAddScaled: halContext.createHalPainterImageCompositor(
                `
            uniform float u_scale;
            
            vec4 computeColor(ivec2 targetPixel) {
                vec4 a = texelFetch0(targetPixel, ADDRESS_MODE_REPEAT);
                vec4 b = texelFetch1(targetPixel, ADDRESS_MODE_REPEAT);
                return a + b * u_scale;
            }
        `,
            ),
            compositorMul: halContext.createHalPainterImageCompositor(
                `
            vec4 computeColor(ivec2 targetPixel) {
                vec4 a = texelFetch0(targetPixel, ADDRESS_MODE_REPEAT);
                vec4 b = texelFetch1(targetPixel, ADDRESS_MODE_REPEAT);
                return a * b;
            }
        `,
            ),
            compositorDiv: halContext.createHalPainterImageCompositor(
                `
            vec4 computeColor(ivec2 targetPixel) {
                vec4 a = texelFetch0(targetPixel, ADDRESS_MODE_REPEAT);
                vec4 b = texelFetch1(targetPixel, ADDRESS_MODE_REPEAT);
                return a / b;
            }
        `,
            ),
            compositorSqrt: halContext.createHalPainterImageCompositor(
                `
            vec4 computeColor(ivec2 targetPixel) {
                vec4 a = texelFetch0(targetPixel, ADDRESS_MODE_REPEAT);
                return sqrt(a);
            }
        `,
            ),
            compositorClampMin: halContext.createHalPainterImageCompositor(
                `
            uniform float u_min;

            vec4 computeColor(ivec2 targetPixel) {
                vec4 a = texelFetch0(targetPixel, ADDRESS_MODE_REPEAT);
                return max(a, u_min);
            }
        `,
            ),
        }
    }

    dispose() {
        if (!this._data) {
            throw new Error("ArithmeticHelperHal: Already disposed")
        }
        this._data.compositorScale.release()
        this._data.compositorAddScaled.release()
        this._data.compositorMul.release()
        this._data.compositorDiv.release()
        this._data.compositorSqrt.release()
        this._data.compositorClampMin.release()
        this._data = null
    }

    scale(args: {operand: HalImage; scale: number; result: HalImage}) {
        if (!this._data) {
            throw new Error("ArithmeticHelperHal: Already disposed")
        }
        this._data.compositorScale.ref.paint({
            target: args.result,
            sourceImages: [args.operand],
            parameters: {
                u_scale: {type: "float", value: args.scale},
            },
        })
    }

    addScaled(args: {operandA: HalImage; operandB: HalImage; scale: number; result: HalImage}) {
        if (!this._data) {
            throw new Error("ArithmeticHelperHal: Already disposed")
        }
        this._data.compositorAddScaled.ref.paint({
            target: args.result,
            sourceImages: [args.operandA, args.operandB],
            parameters: {
                u_scale: {type: "float", value: args.scale},
            },
        })
    }

    add(args: {operandA: HalImage; operandB: HalImage; result: HalImage}) {
        this.addScaled({
            ...args,
            scale: 1,
        })
    }

    sub(args: {operandA: HalImage; operandB: HalImage; result: HalImage}) {
        this.addScaled({
            ...args,
            scale: -1,
        })
    }

    mul(args: {operandA: HalImage; operandB: HalImage; result: HalImage}) {
        if (!this._data) {
            throw new Error("ArithmeticHelperHal: Already disposed")
        }
        this._data.compositorMul.ref.paint({
            target: args.result,
            sourceImages: [args.operandA, args.operandB],
        })
    }

    div(args: {operandA: HalImage; operandB: HalImage; result: HalImage}) {
        if (!this._data) {
            throw new Error("ArithmeticHelperHal: Already disposed")
        }
        this._data.compositorDiv.ref.paint({
            target: args.result,
            sourceImages: [args.operandA, args.operandB],
        })
    }

    sqrt(args: {operand: HalImage; result: HalImage}) {
        if (!this._data) {
            throw new Error("ArithmeticHelperHal: Already disposed")
        }
        this._data.compositorSqrt.ref.paint({
            target: args.result,
            sourceImages: [args.operand],
        })
    }

    // Clamps values up to at least `min` - e.g. clampMin(x, 0) turns a tiny-negative floating-point
    // cancellation artifact into exactly 0 without masking a genuinely-wrong negative value the way abs()
    // would (abs() would also flip a real bug's negative result into a plausible-looking wrong positive one).
    clampMin(args: {operand: HalImage; min: number; result: HalImage}) {
        if (!this._data) {
            throw new Error("ArithmeticHelperHal: Already disposed")
        }
        this._data.compositorClampMin.ref.paint({
            target: args.result,
            sourceImages: [args.operand],
            parameters: {
                u_min: {type: "float", value: args.min},
            },
        })
    }

    // private getResultImageDescriptor(...sources: HalImagePtr[]): PartialHalImageDescriptor {
    //     const maxWidth = Math.max(...sources.map((s) => s.halImage.width))
    //     const maxHeight = Math.max(...sources.map((s) => s.halImage.height))
    //     const maxChannelLayout = sources.reduce<HalImageChannelLayout>(
    //         (acc, s) => (acc.length > s.halImage.descriptor.channelLayout.length ? acc : s.halImage.descriptor.channelLayout),
    //         "R" as HalImageChannelLayout,
    //     )
    //     return {
    //         width: maxWidth,
    //         height: maxHeight,
    //         channelLayout: maxChannelLayout,
    //         dataType: "float32",
    //     }
    // }

    private _data: Data | null
}

type Data = {
    compositorScale: HalPainterImageCompositorRef
    compositorAddScaled: HalPainterImageCompositorRef
    compositorMul: HalPainterImageCompositorRef
    compositorDiv: HalPainterImageCompositorRef
    compositorSqrt: HalPainterImageCompositorRef
    compositorClampMin: HalPainterImageCompositorRef
}
